import { useMemo } from "react"
import { FormControl, FormDescription, FormItem, FormLabel, FormMessage, useFormField } from "@ui/shad"
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form"

import { useFetchBooks } from "@/hooks/api/books"

import { SelectInput } from "../inputs/select-input"

interface BookSelectFieldProps<T extends FieldValues> {
  id: string
  label?: string
  description?: string
  placeholder?: string
  field: ControllerRenderProps<T, Path<T>>
  bookIds?: string[]
}

export function BookSelectField<T extends FieldValues>({
  id,
  label = "Book",
  description,
  placeholder,
  field,
  bookIds,
}: BookSelectFieldProps<T>) {
  const { data: books, isLoading } = useFetchBooks()
  const { error } = useFormField()

  const options = useMemo(() => {
    return (
      books?.books
        ?.filter((book) => !bookIds || bookIds.includes(book.id))
        .map((book) => ({
          value: book.id,
          label: book.name,
        })) ?? []
    )
  }, [bookIds, books?.books])

  return (
    <FormItem>
      {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
      <FormControl>
        <SelectInput
          id={id}
          value={field.value}
          onChange={field.onChange}
          options={options}
          placeholder={placeholder || "Select book"}
          disabled={isLoading}
          error={!!error}
        />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  )
}
